import { useNavigate } from 'react-router-dom';
import { theme } from '../theme.js';
import { useAuth, useAccess } from '../context/AuthContext.js';
import { useCheckRuns } from '../lib/queries.js';
import type { CheckRunSummary } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { assetTypeIcon } from '../lib/asset-types.js';
import { AdminOverview } from '../components/AdminOverview.js';
import { actorLabel } from '../lib/people.js';

/**
 * Checks: start a check, the team's active checks, recently submitted, and
 * (admins) what needs attention — overdue checks, open issues, equipment due.
 */
export function ChecksPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const { isAdmin } = useAccess();
  const { data: runs = [], isLoading } = useCheckRuns(orgId, { status: 'draft' });
  // Non-admins' run lists are already limited to their own team by the API.
  const { data: submitted = [] } = useCheckRuns(orgId, { status: 'submitted' });

  return (
    <PageShell>
      <div style={{
        padding: '14px 18px 12px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, letterSpacing: -0.5 }}>Checks</div>
        <button onClick={() => nav('/checks/new')} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none',
          background: theme.accent, color: theme.accentInk, fontSize: 13, fontWeight: 700, fontFamily: theme.font, cursor: 'pointer',
        }}>
          <Icon name="plus" size={15} /> Start a check
        </button>
      </div>

      {/* body */}
      <div style={{ flex: 1, overflowY: 'auto', background: theme.surface }}>
        {isAdmin && <AdminOverview orgId={orgId} />}
        <SectionLabel>Active checks</SectionLabel>

        {isLoading && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
            Loading…
          </div>
        )}
        {!isLoading && runs.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
            No active checks — all done or none assigned yet.
          </div>
        )}
        {runs.map((run, i) => (
          <RunCard
            key={run.id}
            run={run}
            last={i === runs.length - 1}
            onClick={() => nav(`/checks/${run.id}`)}
          />
        ))}

        {submitted.length > 0 && (
          <>
            <SectionLabel>Recently submitted</SectionLabel>
            {submitted.slice(0, 5).map((run) => (
              <button key={run.id} onClick={() => nav(`/checks/${run.id}`)} style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                padding: '11px 20px', background: theme.surface, border: 'none', cursor: 'pointer',
                borderBottom: `1px solid ${theme.border}`, fontFamily: theme.font,
              }}>
                <Icon name={assetTypeIcon(run.assetType)} size={18} style={{ color: theme.textMuted, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{run.assetName}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted }}>
                    {run.teamName ?? 'Any crew'} · {actorLabel(run.submittedByName, run.submittedByActive)}
                    {run.submittedAt ? ` · ${fmtDate(run.submittedAt)}` : ''}
                  </div>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, flexShrink: 0,
                  background: run.issueCount ? theme.status.danger.bg : theme.status.ok.bg,
                  color: run.issueCount ? theme.status.danger.fg : theme.status.ok.fg,
                }}>
                  {run.issueCount ? `${run.issueCount} issue${run.issueCount > 1 ? 's' : ''}` : 'All OK'}
                </span>
              </button>
            ))}
          </>
        )}
      </div>
    </PageShell>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 600, color: theme.textDim,
      padding: '16px 20px 8px', background: theme.bg,
      borderTop: `1px solid ${theme.border}`,
      borderBottom: `1px solid ${theme.border}`,
    }}>
      {children}
    </div>
  );
}

function RunCard({ run, last, onClick }: {
  run: CheckRunSummary; last: boolean; onClick: () => void;
}) {
  const checked = run.checkedCount ?? 0;
  const total   = run.itemCount   ?? 0;
  const issues  = run.issueCount  ?? 0;
  const pct     = total > 0 ? (checked / total) * 100 : 0;

  return (
    <div
      onClick={onClick}
      style={{
        padding: '14px 20px',
        borderBottom: last ? 'none' : `1px solid ${theme.border}`,
        cursor: 'pointer',
      }}
    >
      {/* top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: theme.surface2, border: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textDim,
        }}>
          <Icon name={assetTypeIcon(run.assetType)} size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, lineHeight: 1.3 }}>
            {run.assetName ?? 'Asset'}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
            {run.teamName ?? 'Any crew'} · {run.scheduleId ? `from ${fmtDate(run.periodStart)}` : `on demand · ${fmtDate(run.periodStart)}`}
          </div>
        </div>
        <Icon name="chevron" size={16} style={{ color: theme.textMuted, flexShrink: 0, marginTop: 3 }} />
      </div>

      {/* progress bar */}
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 12, color: theme.textDim, fontWeight: 500 }}>
            {checked} of {total} checked
          </span>
          {issues > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: theme.status.danger.fg,
              background: theme.status.danger.bg,
              padding: '2px 8px', borderRadius: 999,
            }}>
              {issues} issue{issues !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{
          width: '100%', height: 5, background: theme.bg,
          borderRadius: 999, overflow: 'hidden',
          border: `1px solid ${theme.border}`,
        }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: pct === 100 ? theme.status.ok.dot : theme.accent,
            transition: 'width .3s',
          }} />
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

